import { z } from "zod";
import { parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { PAID_BY_JOINT_SELECT_VALUE } from "@apps/dashboard/lib/paid-by-select";
import {
  endsOnForMonthlyFixedWindow,
  listOccurrenceDates,
  monthsStepForFrequency,
  type ExpenseRecurrenceFrequency,
} from "@apps/dashboard/lib/recurring-dates";

const frequencySchema = z.enum([
  "monthly",
  "bimonthly",
  "quarterly",
  "semiannual",
  "annual",
]);

const recurrenceKindSchema = z.enum(["monthly_fixed", "series"]);

export type ExpenseRecurrenceKind = z.infer<typeof recurrenceKindSchema>;

export type ExpenseNewFormValues = {
  title: string;
  amountInput: string;
  currency: string;
  occurredOn: string;
  categoryId: string;
  paymentMethod: "" | "card" | "cash" | "account";
  shared: boolean;
  isRecurring: boolean;
  recurrenceKind: ExpenseRecurrenceKind;
  endsOn: string;
  frequency: ExpenseRecurrenceFrequency;
  prorated: boolean;
  paidByUserId: string;
  notes: string;
  paidNow: boolean;
};

type CategoryLike = { id: string; name: string };

export function createExpenseNewSchema(
  t: (key: string) => string,
  categories: CategoryLike[],
) {
  return z
    .object({
      title: z.string(),
      amountInput: z.string(),
      currency: z.string(),
      occurredOn: z.string(),
      categoryId: z.string(),
      paymentMethod: z.union([z.literal(""), z.enum(["card", "cash", "account"])]),
      shared: z.boolean(),
      isRecurring: z.boolean(),
      recurrenceKind: recurrenceKindSchema,
      endsOn: z.string(),
      frequency: frequencySchema,
      prorated: z.boolean(),
      paidByUserId: z.union([
        z.literal(""),
        z.literal(PAID_BY_JOINT_SELECT_VALUE),
        z.string().uuid(),
      ]),
      notes: z.string(),
      paidNow: z.boolean(),
    })
    .superRefine((data, ctx) => {
      const catName = categories.find((c) => c.id === data.categoryId)?.name?.trim() ?? "";
      const resolvedTitle = data.title.trim() || catName;
      if (!resolvedTitle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["title"],
          message: t("dashboardUi.expenses.form.titleOrCategoryRequired"),
        });
      }

      const minor = parseAmountToMinor(data.amountInput);
      if (minor === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["amountInput"],
          message: t("dashboardUi.expenses.form.invalidAmount"),
        });
      }

      const ccy = data.currency.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(ccy)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["currency"],
          message: t("dashboardUi.auth.errors.generic"),
        });
      }

      if (!data.isRecurring) return;

      if (data.recurrenceKind === "monthly_fixed") {
        const endsVirtual = endsOnForMonthlyFixedWindow(data.occurredOn);
        const dates = listOccurrenceDates(data.occurredOn, endsVirtual, 1);
        if (dates.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["occurredOn"],
            message: t("dashboardUi.expenses.form.recurrenceInvalid"),
          });
        }
        return;
      }

      if (data.prorated && !data.endsOn?.trim()) {
        const step = monthsStepForFrequency(data.frequency);
        if (step <= 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endsOn"],
            message: t("dashboardUi.expenses.form.recurringProratedRequiresEndsOn"),
          });
        }
        return;
      }

      const endsTrim = data.endsOn?.trim() ?? "";
      if (!endsTrim) {
        return;
      }
      if (endsTrim < data.occurredOn) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endsOn"],
          message: t("dashboardUi.expenses.form.recurrenceInvalid"),
        });
        return;
      }
      const step = monthsStepForFrequency(data.frequency);
      const dates = listOccurrenceDates(data.occurredOn, endsTrim, step);
      if (dates.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endsOn"],
          message: t("dashboardUi.expenses.form.recurrenceInvalid"),
        });
      }
    });
}
